'use client';

import cx from 'classnames';
import { ChangeEventHandler, MouseEventHandler, ReactNode, useEffect, useRef, useState, useTransition } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useClickAway } from 'react-use';

import { useResponsive } from '@flipster/react/misc/responsive/legacy';

import { PopupToDrawer } from '../PopupToDrawer';
import SuccessIcon from '../assets/svgs/success.svg';
import TriangleDownIcon from '../assets/svgs/triangle-down.svg';
import { SelectBoxProps } from './SelectBox';
import styles from './selectBox.module.scss';

interface Props extends Omit<SelectBoxProps, 'withHover' | 'selected' | 'placeholder' | 'icon' | 'allLabel'> {
  prefix?: ReactNode;
  suffix?: ReactNode;
  value?: string;
  onChange?: (val: string) => void;
  withFocusOpen?: boolean;
  placeholder?: string;
}

export const SelectInputBox = ({
  size = 'large',
  shape = 'default',
  title,
  items,
  value,
  prefix,
  suffix,
  onChange: onChangeCallback,
  onSelect,
  placeholder,
  disabled,
  withFocusOpen = false,
  withMaxHeight = true,
  withCheckedIcon,
  checkDisabled,
  inner,
  isFull,
  listTheme = 'dark',
  listClassName,
  className,
}: Props) => {
  const [, startTransition] = useTransition();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { isMobile } = useResponsive();

  const isDrawer = isMobile && !!title;

  useClickAway(dropdownRef, () => !isDrawer && setIsOpen(false));

  // multi rows 를 위한 방식
  // inner 일때만 동작
  useEffect(() => {
    if (!inner) return;
    startTransition(() => {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    });
  }, [inner, value]);

  const onChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    const { value: newValue } = e.currentTarget;
    onChangeCallback?.(newValue);
    if (isOpen) setIsOpen(false);
  };

  const onClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    onSelect?.(e.currentTarget.value);
    setIsOpen(false);
  };

  const isVisibleList = items.length > 0;

  return (
    <div
      className={cx(styles.wrapper, styles[size], className, {
        [styles.inner]: inner,
        [styles.isFull]: isFull,
        [styles.withMaxHeight]: withMaxHeight,
        [styles.disabled]: disabled,
      })}
      ref={dropdownRef}
    >
      <div className={cx(styles.select, styles[shape], { [styles.isOpen]: isOpen })}>
        {prefix}
        <textarea
          value={value}
          onChange={onChange}
          onClick={() => withFocusOpen && !isDrawer && isVisibleList && setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete='off'
          autoCorrect='off'
          ref={textareaRef}
          rows={1}
        />
        {suffix}
        {isVisibleList && (
          <button
            type='button'
            aria-label='open'
            onClick={() => setIsOpen((prevState) => !prevState)}
            disabled={disabled}
            className={styles.icon}
          >
            <TriangleDownIcon />
          </button>
        )}
      </div>
      <PopupToDrawer
        theme={listTheme}
        title={title}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        popupClassName={listClassName}
        drawerClassName={cx(styles.drawerWrapper, listClassName)}
        withArrow={false}
      >
        <ul className={cx(styles.list, styles[listTheme])}>
          {items.map((item) => (
            <li key={item.value}>
              <button
                type='button'
                value={item.value}
                onClick={onClick}
                className={cx(styles.item, { [styles.selected]: value?.toString() === item.value.toString() })}
                disabled={checkDisabled?.(item.value)}
              >
                {item.label}
                {withCheckedIcon && isDrawer && value === item.value && <SuccessIcon className={styles.checkIcon} />}
              </button>
            </li>
          ))}
        </ul>
      </PopupToDrawer>
    </div>
  );
};

interface ControlProps extends Omit<Props, 'value'> {
  name: string;
}

export const ControlSelectInputBox = ({ name, onChange: onChangeCallback, onSelect, ...restProps }: ControlProps) => {
  const { control } = useFormContext();
  const {
    field: { value, onChange },
  } = useController({ name, control });

  return (
    <SelectInputBox
      value={value}
      onChange={(newValue) => {
        onChange(newValue);
        onChangeCallback?.(newValue);
      }}
      onSelect={(newValue) => {
        onChange(newValue);
        onSelect?.(newValue);
      }}
      {...restProps}
    />
  );
};
